import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

type Props = {
  open: boolean;
  apiKey: string;
  onClose: () => void;
};

export default function ApiKeyRevealDialog({ open, apiKey, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="api-key-reveal-dialog-title"
      maxWidth="sm"
      fullWidth
      data-testid="api-key-reveal-dialog"
    >
      <DialogTitle id="api-key-reveal-dialog-title">
        API key created — save it now
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This is the only time this key is shown. Once you close this dialog,
          it cannot be retrieved. Copy it now and share it with the partner.
        </Alert>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            value={apiKey}
            label="API key"
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
                sx: { fontFamily: "monospace" },
              },
            }}
            inputProps={{
              "aria-label": "API key",
              "data-testid": "api-key-value",
            }}
            onFocus={(event) => event.target.select()}
          />
          <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
            <IconButton
              onClick={handleCopy}
              aria-label="Copy API key"
              data-testid="copy-api-key-button"
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          I&apos;ve saved it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
